/* Kindly video story: plays the video when it scrolls into view (muted), keeps the active
   step in sync with the video time, fills its progress line, and lets visitors click a step
   to jump to that moment. Pause and sound buttons sit on the video. */
(function () {
  if (!window.customElements || customElements.get('kindly-video-story')) return;

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class KindlyVideoStory extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = '1';

      this.video = this.querySelector('video');
      this.steps = Array.prototype.slice.call(this.querySelectorAll('[data-kvs-step]'));
      this.now = this.querySelector('[data-kvs-now]');
      this.mdesc = this.querySelector('[data-kvs-mdesc]');
      this.playBtn = this.querySelector('[data-kvs-play]');
      this.soundBtn = this.querySelector('[data-kvs-sound]');
      this.starts = this.steps.map(function (li) { return parseFloat(li.dataset.start) || 0; });
      this.active = -1;
      this.userPaused = false;
      this.visible = false;
      this.raf = 0;

      this.setActive(0, true);
      if (!this.video) return;

      this.video.muted = true;
      this.video.setAttribute('muted', '');
      this.video.loop = true;
      this.video.playsInline = true;
      // Shopify's video_tag can include its own controls; the section has its own buttons.
      this.video.removeAttribute('controls');

      this.tick = this.tick.bind(this);
      this.video.addEventListener('play', this.onPlayState.bind(this));
      this.video.addEventListener('pause', this.onPlayState.bind(this));
      this.video.addEventListener('timeupdate', this.sync.bind(this));
      this.video.addEventListener('seeked', this.sync.bind(this));
      this.video.addEventListener('loadedmetadata', this.sync.bind(this));

      if (this.playBtn) this.playBtn.addEventListener('click', this.togglePlay.bind(this));
      if (this.soundBtn) this.soundBtn.addEventListener('click', this.toggleSound.bind(this));
      this.steps.forEach(function (li, i) {
        var btn = li.querySelector('.kvs__step-btn');
        if (btn) btn.addEventListener('click', this.jump.bind(this, i));
      }, this);

      var autoplay = this.dataset.autoplay === 'true' && !reduceMotion;
      if (!autoplay) this.userPaused = true;
      this.onPlayState();

      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            this.visible = e.isIntersecting;
            if (this.visible) {
              if (this.video.preload === 'none') this.video.preload = 'auto';
              if (!this.userPaused) this.play();
            } else if (!this.video.paused) {
              this.video.pause();
            }
          }, this);
        }.bind(this), { threshold: 0.35 });
        this.io.observe(this.querySelector('.kvs__card') || this);
      } else if (autoplay) {
        this.play();
      }
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      cancelAnimationFrame(this.raf);
    }

    play() {
      var p = this.video.play();
      if (p && p.catch) p.catch(function () { /* autoplay blocked: leave the play button showing */ });
    }

    togglePlay() {
      if (this.video.paused) {
        this.userPaused = false;
        if (this.video.preload === 'none') this.video.preload = 'auto';
        this.play();
      } else {
        this.userPaused = true;
        this.video.pause();
      }
    }

    toggleSound() {
      var on = this.video.muted;
      this.video.muted = !on;
      if (on) {
        this.video.removeAttribute('muted');
        if (this.video.paused) { this.userPaused = false; this.play(); }
      } else {
        this.video.setAttribute('muted', '');
      }
      this.classList.toggle('has-sound', on);
      this.soundBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
      this.soundBtn.setAttribute('aria-label', on ? this.soundBtn.dataset.labelOff : this.soundBtn.dataset.labelOn);
    }

    jump(i) {
      if (!this.video || !this.steps[i]) return;
      if (this.video.preload === 'none') this.video.preload = 'auto';
      var t = this.starts[i];
      var go = function () {
        try { this.video.currentTime = t + 0.05; } catch (e) {}
        this.setActive(i);
        this.userPaused = false;
        this.play();
      }.bind(this);
      if (this.video.readyState >= 1) go();
      else {
        this.video.addEventListener('loadedmetadata', go, { once: true });
        this.video.load();
      }
    }

    onPlayState() {
      var paused = this.video.paused;
      this.classList.toggle('is-paused', paused);
      if (this.playBtn) {
        this.playBtn.setAttribute('aria-label', paused ? this.playBtn.dataset.labelPlay : this.playBtn.dataset.labelPause);
      }
      cancelAnimationFrame(this.raf);
      if (!paused) this.raf = requestAnimationFrame(this.tick);
    }

    tick() {
      this.sync();
      if (!this.video.paused) this.raf = requestAnimationFrame(this.tick);
    }

    indexAt(t) {
      var idx = 0;
      for (var i = 0; i < this.starts.length; i++) if (t >= this.starts[i]) idx = i;
      return idx;
    }

    sync() {
      if (!this.steps.length) return;
      var t = this.video.currentTime || 0;
      var dur = this.video.duration;
      var i = this.indexAt(t);
      if (i !== this.active) this.setActive(i);
      var start = this.starts[i];
      var end = i + 1 < this.starts.length ? this.starts[i + 1] : (isFinite(dur) && dur > start ? dur : start + 1);
      var p = Math.max(0, Math.min(1, (t - start) / Math.max(0.1, end - start)));
      this.steps[i].style.setProperty('--kvs-p', p.toFixed(4));
    }

    setActive(i, initial) {
      if (!this.steps[i]) return;
      this.active = i;
      this.steps.forEach(function (li, n) {
        li.classList.toggle('is-active', n === i);
        if (n !== i) li.style.setProperty('--kvs-p', '0');
        var btn = li.querySelector('.kvs__step-btn');
        if (btn) {
          if (n === i) btn.setAttribute('aria-current', 'step');
          else btn.removeAttribute('aria-current');
        }
      });
      var li = this.steps[i];
      if (this.now) this.now.textContent = li.dataset.num + ' · ' + li.dataset.title;
      if (this.mdesc) {
        var d = li.querySelector('.kvs__desc p');
        this.mdesc.textContent = d ? d.textContent : '';
      }
      // On phones the steps are a scrolling row: keep the active pill in view.
      if (!initial) {
        var row = li.parentElement;
        if (row && row.scrollWidth > row.clientWidth + 4) {
          row.scrollTo({ left: li.offsetLeft - row.firstElementChild.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
        }
      }
    }
  }

  customElements.define('kindly-video-story', KindlyVideoStory);
})();
